import { Injectable } from '@angular/core';
import {Router, NavigationEnd, NavigationStart} from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ScrollPositionService {
  private scrollPositions = new Map<string, number>();
  private currentPath: string;
  private onScrollBound = this.onScroll.bind(this);
  private navigationTrigger: 'imperative' | 'popstate' | 'hashchange' = 'imperative';

  constructor(private router: Router) {
    // Track route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.navigationTrigger = event.navigationTrigger;
      });
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const newPath = event.urlAfterRedirects;

        // Store the current scroll position before navigating
        if (this.currentPath && this.currentPath !== newPath) {
          this.saveScrollPosition(this.currentPath);
        }

        this.currentPath = newPath;

        // Restore scroll position after a short delay to allow DOM to render
        setTimeout(() => {
          if (this.navigationTrigger === 'popstate') {
            this.restoreScrollPosition(newPath);
          }
        }, 100);
      });
  }

  initialize() {
    // Start listening to scroll events
    document.addEventListener('scroll', this.onScrollBound, true);
  }

  destroy() {
    // Clean up scroll listener
    document.removeEventListener('scroll', this.onScrollBound, true);
  }

  private onScroll() {
    if (this.currentPath) {
      const scrollElement = this.getScrollElement();
      if (scrollElement) {
        this.scrollPositions.set(this.currentPath, scrollElement.scrollTop);
      }
    }
  }

  private saveScrollPosition(path: string) {
    const scrollElement = this.getScrollElement();
    if (scrollElement) {
      this.scrollPositions.set(path, scrollElement.scrollTop);
    }
  }

  private restoreScrollPosition(path: string) {
    const scrollPosition = this.scrollPositions.get(path);
    if (scrollPosition !== undefined) {
      const scrollElement = this.getScrollElement();
      if (scrollElement) {
        scrollElement.scrollTop = scrollPosition;
      }
    }
  }

  private getScrollElement(): Element | null {
    // Try to find mat-sidenav-content or other scrollable container
    return document.querySelector('mat-sidenav-content') ||
           document.querySelector('.mat-sidenav-content') ||
           document.documentElement;
  }

  clearScrollPosition(path?: string) {
    if (path) {
      this.scrollPositions.delete(path);
    } else {
      this.scrollPositions.clear();
    }
  }

  getStoredScrollPosition(path?: string): number | undefined {
    return this.scrollPositions.get(path || this.currentPath);
  }
}
